import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { Outlet, useParams } from "react-router-dom";
import { fetchMemberLinkSocketToken, searchTripsByMrn } from "services/memberMrn";
import { socketService, Events } from "services/socket";
import { ToastMemberLinkNotification } from "utils/toasts";

export const FundingSourceContext = createContext(null);

export const useFundingSourceContext = () => {
  const context = useContext(FundingSourceContext);
  if (!context) {
    throw new Error(
      "useFundingSourceContext must be used within FundingSourceLayout"
    );
  }
  return context;
};

/**
 * FundingSourceLayout
 *
 * Wraps all routes under /fundingsource/:fundingsourceId/*
 * Manages WebSocket connection lifecycle across all child routes.
 */
export default function FundingSourceLayout() {
  const { fundingsourceId, memberId } = useParams();

  const [isConnected, setIsConnected] = useState(socketService.isConnected);
  const [isConnecting, setIsConnecting] = useState(false);
  const [activeMrn, setActiveMrn] = useState(memberId || null);
  const [latestNotification, setLatestNotification] = useState(null);
  const resolvedClientIdRef = useRef(memberId || null);

  // Sync active MRN when route changes. When returning to search screen, resets activeMrn to null.
  useEffect(() => {
    setActiveMrn(memberId || null);
    resolvedClientIdRef.current = memberId || null;
  }, [memberId]);

  // Connect socket when we have valid fundingsourceId and active MRN (logged-in member)
  useEffect(() => {
    if (!fundingsourceId || !activeMrn) {
      console.log(
        "%c[FundingSourceLayout] No active MRN. Ensuring socket is disconnected.",
        "color: #6B7280; font-style: italic;"
      );
      socketService.disconnectSocket();
      setIsConnected(false);
      setIsConnecting(false);
      return;
    }

    let isCancelled = false;

    async function establishSocketConnection() {
      setIsConnecting(true);

      try {
        console.log(
          `%c[FundingSourceLayout] Fetching socket credentials & client _id for Cooperate ID: ${fundingsourceId}, MRN: ${activeMrn}`,
          "color: #3B82F6; font-weight: bold;"
        );

        // 1. Fetch socket auth token & client profile (containing client._id) in parallel
        const [socketAuthData, tripsData] = await Promise.all([
          fetchMemberLinkSocketToken(fundingsourceId),
          searchTripsByMrn(activeMrn, fundingsourceId).catch((err) => {
            console.warn(
              "[FundingSourceLayout] Unable to load client details for socket clientId:",
              err
            );
            return null;
          }),
        ]);

        if (isCancelled) return;

        // Extract client MongoDB _id from trips API response
        const clientObjectId =
          tripsData?.client?._id ||
          tripsData?.client?.id ||
          memberId ||
          activeMrn;

        resolvedClientIdRef.current = clientObjectId;

        console.log(
          `%c[FundingSourceLayout] Resolved Client _id from trips API: ${clientObjectId} (MRN: ${activeMrn})`,
          "color: #10B981; font-weight: bold;"
        );

        const token =
          socketAuthData?.token ||
          socketAuthData?.data?.token ||
          socketAuthData?.socketToken ||
          socketAuthData?.jwt ||
          (typeof socketAuthData === "string" ? socketAuthData : "");

        const serverUrl =
          socketAuthData?.url ||
          socketAuthData?.socketUrl ||
          socketAuthData?.data?.url ||
          null;

        // 2. Connect singleton Socket.IO instance for this client _id
        socketService.connectSocket({
          token,
          serverUrl,
          extraData: {
            cooperateId: fundingsourceId,
            clientId: clientObjectId,
            mrn: activeMrn,
            ...(typeof socketAuthData === "object" ? socketAuthData : {}),
          },
          extraHeaders: {
            "x-cooperate-id": fundingsourceId,
          },
        });
      } catch (error) {
        if (!isCancelled) {
          console.error(
            `%c[FundingSourceLayout] Socket connection failed for Cooperate ID ${fundingsourceId} / MRN ${activeMrn}:`,
            "color: #EF4444; font-weight: bold;",
            error
          );
        }
      } finally {
        if (!isCancelled) {
          setIsConnecting(false);
        }
      }
    }

    establishSocketConnection();

    // 3. Track socket connection status & Join Room on connect
    const unsubConnect = socketService.addListener(Events.CONNECT, () => {
      if (!isCancelled) {
        setIsConnected(true);
        const currentClientId = resolvedClientIdRef.current || activeMrn;
        // Emit JOIN_ROOM when socket is actually connected
        socketService.emit(Events.JOIN_ROOM, {
          cooperateId: fundingsourceId,
          clientId: currentClientId,
          mrn: activeMrn,
        });
      }
    });

    const unsubDisconnect = socketService.addListener(Events.DISCONNECT, () => {
      if (!isCancelled) setIsConnected(false);
    });

    const unsubConnectError = socketService.addListener(
      Events.CONNECT_ERROR,
      () => {
        if (!isCancelled) setIsConnected(false);
      }
    );

    // 4. Listen to memberLinkNotification events
    const unsubMemberLinkNotification = socketService.addListener(
      Events.MEMBER_LINK_NOTIFICATION,
      (payload) => {
        console.log(
          "%c[FundingSourceLayout] NOTIFICATION RECEIVED (memberLinkNotification):",
          "background: #10B981; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;",
          payload
        );
        if (!isCancelled) {
          setLatestNotification(payload);
          ToastMemberLinkNotification(payload);
        }
      }
    );

    // 5. Cleanup: Whenever MRN changes or unmounts, disconnect previous socket session & purge listeners
    return () => {
      isCancelled = true;
      unsubConnect();
      unsubDisconnect();
      unsubConnectError();
      unsubMemberLinkNotification();
      socketService.disconnectSocket();
      setIsConnected(false);
    };
  }, [fundingsourceId, activeMrn, memberId]);

  // Clean disconnect when leaving funding source entirely
  useEffect(() => {
    return () => {
      socketService.disconnectSocket();
    };
  }, [fundingsourceId]);

  const value = {
    fundingsourceId,
    activeMrn,
    setActiveMrn,
    isConnected,
    isConnecting,
    latestNotification,
    socketService,
  };

  return (
    <FundingSourceContext.Provider value={value}>
      <Outlet />
    </FundingSourceContext.Provider>
  );
}
